import { Component, ChangeDetectionStrategy, signal, inject, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, RouterLinkActive } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../../core/services/auth';
import { UserRole } from '../../../core/models/auth_model';

interface MenuItem {
  label: string;
  icon: string; // Nombre del icono (usaremos SVGs o una librería)
  route?: string;
  children?: MenuItem[];
  expanded?: boolean;
  roles?: UserRole[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent {
  private authService = inject(AuthService);

  /** Desktop only: the menu shrinks to an icon rail. In the mobile drawer it is always expanded. */
  readonly collapsed = input(false);
  readonly toggleCollapsed = output<void>();

  // Usar computed para reactividad si el usuario cambia (ej. logout/login)
  readonly currentUser = computed(() => this.authService.currentUser());
  readonly isAdmin = computed(() => this.currentUser()?.role === UserRole.ADMIN);

  // Definición del menú
  menuItems = signal<MenuItem[]>([
    {
      label: 'Propiedades',
      icon: 'home',
      expanded: true, // Por defecto expandido para ver sub-opciones
      children: [
        { label: 'Agregar propiedad', icon: 'add_circle', route: '/internal/properties' },
        { label: 'Listado', icon: 'list', route: '/internal/inventory' }
      ]
    },
    {
      label: 'Gestión',
      icon: 'settings',
      expanded: true,
      children: [
        { 
          label: 'Contenido', 
          icon: 'category', 
          route: '/internal/manage-content',
          roles: [UserRole.ADMIN] 
        },
        { 
          label: 'Contactos', 
          icon: 'contact_mail', 
          route: '/internal/contacts',
          roles: [UserRole.ADMIN] 
        },
        { 
          label: 'Usuarios', 
          icon: 'people', 
          route: '/internal/manage-users',
          roles: [UserRole.ADMIN] 
        },
        {
          label: 'Ubicaciones',
          icon: 'place',
          route: '/internal/manage-locations',
          roles: [UserRole.ADMIN]
        }
      ]
    }
  ]);

  toggleSubmenu(item: MenuItem) {
    if (item.children) {
      item.expanded = !item.expanded;
      // Forzar actualización del signal si fuera necesario, pero al ser objeto mutado dentro del array, 
      // Angular signals a veces requiere una nueva referencia o un update explícito.
      // Dado que modificamos la propiedad expanded in-place y usamos ChangeDetection.OnPush, 
      // lo mejor es actualizar el signal con una copia nueva del array para disparar la detección.
      this.menuItems.update(items => [...items]);
    }
  }

  // Filtrar items según rol
  shouldShow(item: MenuItem): boolean {
    // Si no tiene restricción de roles, mostrar siempre
    if (!item.roles || item.roles.length === 0) return true;
    
    // Obtener el rol del usuario actual
    const userRole = this.currentUser()?.role;
    
    // Si no hay usuario o rol, no mostrar
    if (userRole === null || userRole === undefined) {
      return false;
    }
    
    // Verificar si el rol del usuario está en la lista de roles permitidos
    return item.roles.includes(userRole);
  }

  // Verificar si un grupo padre tiene al menos un hijo visible
  hasVisibleChildren(item: MenuItem): boolean {
    if (!item.children) return false;
    return item.children.some(child => this.shouldShow(child));
  }

  logout() {
    this.authService.logout();
  }
}
